import type { GitFileTree, GitFileTreeWithContents, GitTreeItem } from './models/git-worker.model';

export interface WasmGitModule {
  locateFile: (file: string) => void;
  print: (file: string) => void;
  printErr: (text: string) => void;
  callMain: (params: string[]) => void;
  callAndCaptureOutput: (params: string[]) => string;
  onRuntimeInitialized: () => void;
  // eslint-disable-next-line
  FS: any;
  // eslint-disable-next-line
  emscriptenhttpconnect: any;
}

/**
 * https://emscripten.org/docs/api_reference/Filesystem-API.html
 */
export interface WasmGitFS {
  mkdir: (dir: string, chmod?: string) => void;
  mount: (db: unknown, options: unknown, dir: string) => void;
  syncfs: (read: boolean, doneCb: () => void) => void;
  chdir: (dir: string) => void;
  readdir: (dir: string) => string[];
  cwd: () => string;
  readFile: (path: string, opt?: { encoding?: 'utf8' }) => string;
  writeFile: (path: string, content: string) => void;
  rmdir: (path: string) => void;
  rename: (oldPath: string, newPath: string) => void;
  unlink: (path: string) => void;
  lstat: () => void;
  stat: (path: string) => GitFSStats;
  lookupPath: (path: string, options?: unknown) => GitFSLookupPath;
  analyzePath: (path: string) => { exists: boolean };
  makedev: (arg1: number, arg2: number) => unknown;
}

export interface GitFSStats {
  mtime: Date;
}

export interface GitFSNode {
  isFolder: boolean;
}

export interface GitFSLookupPath {
  path: string;
  node: GitFSNode;
}

export enum GitFSErrno {
  BUSY = 10,
  ENOENT = 44,
}

export interface GitFSErrnoError extends Error {
  errno: GitFSErrno;
}

export type WorkerActionParams<Action extends WorkerAction> = Action['params'];

export interface WasmGitFSUtil {
  syncfs: (read: boolean) => Promise<void>;
  isDir: (path: string) => boolean;
  isFile: (path: string) => boolean;
  itemExistsAt: (path: string) => boolean;
  readFileTree: (
    path: string,
    options?: {
      parent?: GitTreeItem;
      ignoredPaths?: string[];
      ignoredNames?: string[];
      excludeChildren?: boolean;
      predicate?: (item: GitTreeItem, level: number) => boolean;
    },
    level?: number,
  ) => GitFileTree;
  readFileTreeWithContent: (
    path: string,
    options?: {
      ignoredPaths?: string[];
      ignoredFileNames?: string[];
      filePredicate?: (item: GitTreeItem, level: number) => boolean;
    },
  ) => GitFileTreeWithContents<string>;
  getTreeItemAtPath: (path: string) => GitTreeItem;
  mkdirP: (path: string) => void;
  ensureFileExists: (path: string) => void;
  resolvePath: (path: string) => string;
  dirname: (path: string) => string;
  readFile: (path: string) => string;
}

export interface WorkerFnParams<Action extends WorkerAction> {
  lg: WasmGitModule;
  fs: WasmGitFS;
  params: Action['params'];
  idbfs: unknown;
  util: WasmGitFSUtil;
}

export interface WorkerAction<Params = unknown> {
  type: string;
  params?: Params;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WorkerActionFn<Return = void> = (params: WorkerActionParams<any>) => Promise<Return>;

export class WorkerActionError extends Error {
  constructor(err: unknown) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    super(err as any);

    this.errno = (err as GitFSErrnoError).errno;
  }

  errno: GitFSErrno;
}

export interface WorkerResult<T extends WorkerAction> {
  id: number;
  action: T['type'];
  error?: string;
}
